import { z } from 'zod';
import { ValidationPatterns } from '../shared';

/**
 * USER INTERACTION SCHEMAS - Learning Activity Tracking
 * =============================================================================
 * Following Base+Variants pattern from docs/schemas-plan.md
 * Core learning interaction entity that bridges User + Word models
 * Used extensively in stats routes for dashboard, interactions, history
 * =============================================================================
 */

// Interaction type enum - types of learning activities
export enum InteractionType {
  WORD_VIEWED = 'word_viewed',
  WORD_LEARNED = 'word_learned',
  AUDIO_PLAYED = 'audio_played',
  EXAMPLE_VIEWED = 'example_viewed',
  PREFERENCES_UPDATED = 'preferences_updated',
  SESSION_STARTED = 'session_started',
  SESSION_COMPLETED = 'session_completed',
  CAROUSEL_NAVIGATED = 'carousel_navigated',
}

// Interaction source enum - where the interaction originated
export enum InteractionSource {
  BOT = 'bot',
  WEB = 'web',
  API = 'api',
}

const InteractionTypeSchema = z.enum(InteractionType);
const InteractionSourceSchema = z.enum(InteractionSource);

// Validation patterns for interaction-specific fields
const patterns = {
  userId: z.string().min(1), // User identifier
  sessionId: z.string().min(1).max(100), // Session identifier
  metadata: z.record(z.string(), z.unknown()).optional(), // Flexible metadata object
  wordText: z.string().min(1).max(100), // Word/phrase text from lookups
  exampleText: z.string().max(500), // Example sentence from lookups
};

// =============================================================================
// BASE USER INTERACTION SCHEMA - Core learning activity (8 fields from mapper)
// =============================================================================
const BaseUserInteractionSchema = z.object({
  // User and content identifiers
  userId: ValidationPatterns.flexibleObjectId,
  wordId: ValidationPatterns.flexibleObjectId.optional(),
  collectionId: ValidationPatterns.flexibleObjectId.optional(),

  // Interaction classification
  interactionType: InteractionTypeSchema,
  source: InteractionSourceSchema,

  // Timing and session context
  timestamp: ValidationPatterns.flexibleDate,
  sessionId: patterns.sessionId.optional(),

  // Additional data
  metadata: patterns.metadata.optional(),
});

// =============================================================================
// USER INTERACTION VARIANTS - For different usage contexts in stats
// =============================================================================
export const UserInteractionSchemas = {
  /**
   * BASE: Core interaction fields (repository/mapper layer)
   * Direct output from UserProgressMapper.interactionToDomain
   */
  base: BaseUserInteractionSchema,

  /**
   * ENRICHED: Base fields + word lookups (dashboard/UI display)
   * Used by getRecentLearningEvents with word data populated
   * Includes word and example text from database lookups
   */
  enriched: BaseUserInteractionSchema.extend({
    word: patterns.wordText,
    example: patterns.exampleText,
  }),
} as const;

// =============================================================================
// TYPE EXPORTS - Clean TypeScript types
// =============================================================================
export type UserInteraction = z.infer<typeof BaseUserInteractionSchema>;
export type EnrichedUserInteraction = z.infer<
  typeof UserInteractionSchemas.enriched
>;
// Removed unused StatsUserInteraction type
// Removed unused TimelineUserInteraction type
